import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { LearnerAuthProvider, useLearnerAuth } from "@/contexts/LearnerAuthContext";
import { SettingsProvider } from "@/contexts/SettingsContext";
import AdminLogin from "@/pages/AdminLogin";
import AdminLayout from "@/pages/AdminLayout";
import Dashboard from "@/pages/Dashboard";
import GlobalSettings from "@/pages/GlobalSettings";
import CohortList from "@/pages/CohortList";
import CohortDetail from "@/pages/CohortDetail";
import LearnerList from "@/pages/LearnerList";
import LearnerDetail from "@/pages/LearnerDetail";
import LearningExperienceList from "@/pages/LearningExperienceList";
import LearningExperienceForm from "@/pages/LearningExperienceForm";
import AdminUsers from "@/pages/AdminUsers";
import ProfileSettings from "@/pages/ProfileSettings";
import AssessmentTypes from "@/pages/AssessmentTypes";
import Analytics from "@/pages/Analytics";
import ResourceLinks from "@/pages/ResourceLinks";
import TrackLibrary from "@/pages/TrackLibrary";
import TrackDetail from "@/pages/TrackDetail";
import ItemBuilder from "@/pages/ItemBuilder";
import ItemEditor from "@/pages/ItemEditor";
import PlaceholderPage from "@/pages/PlaceholderPage";
import LearningLogic from "@/pages/LearningLogic";
import EpicPractice from "@/pages/EpicPractice";
import ClassroomDashboard from "@/pages/ClassroomDashboard";
import CurriculumManager from "@/pages/CurriculumManager";
import SurveyLibrary from "@/pages/SurveyLibrary";
import SurveyBuilder from "@/pages/SurveyBuilder";
import GhostItems from "@/pages/GhostItems";
import LessonPlanList from "@/pages/LessonPlanList";
import LessonPlanBuilder from "@/pages/LessonPlanBuilder";
import EventList from "@/pages/EventList";
import EventDetail from "@/pages/EventDetail";
import AttendeeCatalogPage from "@/pages/AttendeeCatalogPage";
import EventPrint from "@/pages/EventPrint";
import EventSurveyPrint from "@/pages/EventSurveyPrint";
import EventSurveyDisplay from "@/pages/EventSurveyDisplay";
import EventCheckInDisplay from "@/pages/EventCheckInDisplay";
import EventCheckInMobile from "@/pages/EventCheckInMobile";
import CheckInPage from "@/pages/CheckInPage";
import PublicLessonPlan from "@/pages/PublicLessonPlan";
import LearnerLogin from "@/pages/learner/LearnerLogin";
import LearnerLayout from "@/pages/learner/LearnerLayout";
import LearnerDashboard from "@/pages/learner/LearnerDashboard";
import LearnerLearningExperiences from "@/pages/learner/LearnerLearningExperiences";
import LearnerTrackDetail from "@/pages/learner/LearnerTrackDetail";
import LearnerStandaloneLexp from "@/pages/learner/LearnerStandaloneLexp";
import LearnerLessonPage from "@/pages/learner/LearnerLessonPage";
import LearnerResources from "@/pages/learner/LearnerResources";
import LearnerResults from "@/pages/learner/LearnerResults";
import LearnerSurvey from "@/pages/learner/LearnerSurvey";
import LearnerEpicPractice from "@/pages/learner/LearnerEpicPractice";
import { Toaster } from "sonner";
import VersionUpdateBanner from "@/components/VersionUpdateBanner";
import "@/App.css";

// Owner (platform-realm) portal — fully isolated from admin + learner auth
import { OwnerAuthProvider } from "@/owner/OwnerAuthContext";
import OwnerProtectedRoute from "@/owner/OwnerProtectedRoute";
import OwnerLogin from "@/owner/OwnerLogin";
import OwnerLayout from "@/owner/OwnerLayout";
import OwnerDashboard from "@/owner/pages/Dashboard";
import OwnerOrganizations from "@/owner/pages/Organizations";
import OwnerOrganizationDetail from "@/owner/pages/OrganizationDetail";
import OwnerUsers from "@/owner/pages/Owners";
import OwnerPlatformSettings from "@/owner/pages/PlatformSettings";
import OwnerAuditLog from "@/owner/pages/AuditLog";
import OwnerProfile from "@/owner/pages/Profile";
import OwnerBootReports from "@/owner/pages/BootReports";
import OwnerThemes from "@/owner/pages/Themes";

function ProtectedRoute() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0C10] flex items-center justify-center">
        <div
          className="h-8 w-8 border-2 border-white/20 rounded-full animate-spin"
          style={{ borderTopColor: "var(--primary-color, #4A90D9)" }}
        />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/admin" replace />;
  }

  return <Outlet />;
}

function SystemAdminRoute({ children }) {
  const { user } = useAuth();
  if (user?.role !== "system_admin") {
    return <Navigate to="/admin/dashboard" replace />;
  }
  return children;
}

function FeatureGate({ flag, children }) {
  const { user, loading } = useAuth();
  if (loading) return null; // parent ProtectedRoute already shows the spinner
  const flags = user?.feature_flags || {};
  // If flags haven't hydrated yet (old cached /me), allow through. A flag is
  // "disabled" only when the server explicitly returned `false`. Anything
  // else (missing key, undefined, true) passes. When disabled, silently
  // redirect to dashboard — the sidebar already hides disabled features,
  // so reaching this branch means a direct URL hit. No user-facing "not
  // available" messaging per product directive: hide, don't frustrate.
  if (flags[flag] === false) {
    return <Navigate to="/admin/dashboard" replace />;
  }
  return children;
}

function LearnerProtectedRoute() {
  const { learner, loading } = useLearnerAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F9FC] flex items-center justify-center">
        <div
          className="h-8 w-8 border-2 border-gray-200 rounded-full animate-spin"
          style={{ borderTopColor: "var(--primary-color, #4A90D9)" }}
        />
      </div>
    );
  }

  if (!learner) {
    return <Navigate to="/login" replace />;
  }

  return <Outlet />;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Admin Routes */}
      <Route path="/admin" element={<AdminLogin />} />
      <Route element={<ProtectedRoute />}>
        <Route
          element={
            <SettingsProvider>
              <AdminLayout />
            </SettingsProvider>
          }
        >
          <Route path="/admin/dashboard" element={<Dashboard />} />
          <Route path="/admin/settings" element={<GlobalSettings />} />
          <Route path="/admin/cohorts" element={<FeatureGate flag="learner_management"><CohortList /></FeatureGate>} />
          <Route path="/admin/cohorts/:id" element={<FeatureGate flag="learner_management"><CohortDetail /></FeatureGate>} />
          <Route path="/admin/learners" element={<FeatureGate flag="learner_management"><LearnerList /></FeatureGate>} />
          <Route path="/admin/learners/:id" element={<FeatureGate flag="learner_management"><LearnerDetail /></FeatureGate>} />
          <Route path="/admin/events" element={<FeatureGate flag="learner_management"><EventList /></FeatureGate>} />
          <Route path="/admin/events/attendees" element={<FeatureGate flag="learner_management"><AttendeeCatalogPage /></FeatureGate>} />
          <Route path="/admin/events/:id" element={<FeatureGate flag="learner_management"><EventDetail /></FeatureGate>} />
          <Route path="/admin/events/:id/print" element={<FeatureGate flag="learner_management"><EventPrint /></FeatureGate>} />
          <Route path="/admin/events/:id/survey-print" element={<FeatureGate flag="learner_management"><EventSurveyPrint /></FeatureGate>} />
          <Route path="/admin/learning-experiences" element={<FeatureGate flag="curriculum"><LearningExperienceList /></FeatureGate>} />
          <Route path="/admin/learning-experiences/new" element={<FeatureGate flag="curriculum"><LearningExperienceForm /></FeatureGate>} />
          <Route path="/admin/learning-experiences/:id" element={<FeatureGate flag="curriculum"><LearningExperienceForm /></FeatureGate>} />
          <Route path="/admin/logic" element={<FeatureGate flag="learning_logic"><LearningLogic /></FeatureGate>} />
          <Route path="/admin/tracks" element={<FeatureGate flag="curriculum"><TrackLibrary /></FeatureGate>} />
          <Route path="/admin/curriculum" element={<FeatureGate flag="curriculum"><CurriculumManager /></FeatureGate>} />
          <Route path="/admin/tracks/:id" element={<FeatureGate flag="curriculum"><TrackDetail /></FeatureGate>} />
          <Route path="/admin/resources" element={<FeatureGate flag="resource_library"><ResourceLinks /></FeatureGate>} />
          <Route path="/admin/analytics" element={<FeatureGate flag="analytics"><Analytics /></FeatureGate>} />
          <Route path="/admin/items" element={<FeatureGate flag="question_bank"><ItemBuilder /></FeatureGate>} />
          <Route path="/admin/items/new" element={<FeatureGate flag="question_bank"><ItemEditor /></FeatureGate>} />
          <Route path="/admin/items/:id" element={<FeatureGate flag="question_bank"><ItemEditor /></FeatureGate>} />
          <Route path="/admin/assessment-types" element={<FeatureGate flag="historical_assessments"><AssessmentTypes /></FeatureGate>} />
          <Route path="/admin/surveys" element={<FeatureGate flag="surveys"><SurveyLibrary /></FeatureGate>} />
          <Route path="/admin/surveys/:id" element={<FeatureGate flag="surveys"><SurveyBuilder /></FeatureGate>} />
          <Route path="/admin/ghost-items" element={<FeatureGate flag="question_bank"><GhostItems /></FeatureGate>} />
          <Route path="/admin/lesson-plans" element={<FeatureGate flag="lesson_planner"><LessonPlanList /></FeatureGate>} />
          <Route path="/admin/lesson-plans/:id" element={<FeatureGate flag="lesson_planner"><LessonPlanBuilder /></FeatureGate>} />
          <Route path="/admin/epic-practice" element={<FeatureGate flag="epic_practice"><EpicPractice /></FeatureGate>} />
          <Route path="/admin/classroom" element={<ClassroomDashboard />} />
          <Route path="/admin/users" element={<SystemAdminRoute><AdminUsers /></SystemAdminRoute>} />
          <Route path="/admin/profile" element={<ProfileSettings />} />
        </Route>
      </Route>

      {/* Learner Portal Routes */}
      <Route path="/login" element={<LearnerLogin />} />
      <Route element={<LearnerProtectedRoute />}>
        <Route element={<SettingsProvider><LearnerLayout /></SettingsProvider>}>
          <Route path="/portal/dashboard" element={<LearnerDashboard />} />
          <Route path="/portal/learning" element={<LearnerLearningExperiences />} />
          <Route path="/portal/learning/:lexpId" element={<LearnerStandaloneLexp />} />
          <Route path="/portal/tracks/:trackId" element={<LearnerTrackDetail />} />
          <Route path="/portal/tracks/:trackId/lesson/:lexpId" element={<LearnerLessonPage />} />
          <Route path="/portal/resources" element={<LearnerResources />} />
          <Route path="/portal/results" element={<LearnerResults />} />
          <Route path="/portal/surveys/:surveyId" element={<LearnerSurvey />} />
          <Route path="/portal/epic-practice" element={<LearnerEpicPractice />} />
        </Route>
      </Route>

      {/* Public (unauthenticated) */}
      <Route path="/lesson-plans/:id" element={<PublicLessonPlan />} />
      <Route path="/checkin/:eventId" element={<CheckInPage />} />
      <Route path="/event-checkin/:eventId" element={<EventCheckInMobile />} />
      <Route path="/event-checkin/:eventId/me/:learnerId" element={<EventCheckInMobile />} />
      <Route path="/event-checkin/:eventId/attendee/:attendeeId" element={<EventCheckInMobile />} />
      <Route path="/events/:id/survey" element={<EventSurveyDisplay />} />
      <Route path="/events/:id/checkin" element={<EventCheckInDisplay />} />

      {/* Owner (platform) realm — fully isolated */}
      <Route path="/owner/login" element={<OwnerLogin />} />
      <Route element={<OwnerProtectedRoute />}>
        <Route element={<OwnerLayout />}>
          <Route path="/owner/dashboard" element={<OwnerDashboard />} />
          <Route path="/owner/organizations" element={<OwnerOrganizations />} />
          <Route path="/owner/organizations/:id" element={<OwnerOrganizationDetail />} />
          <Route path="/owner/users" element={<OwnerUsers />} />
          <Route path="/owner/platform-settings" element={<OwnerPlatformSettings />} />
          <Route path="/owner/themes" element={<OwnerThemes />} />
          <Route path="/owner/audit-log" element={<OwnerAuditLog />} />
          <Route path="/owner/dev-utility" element={<OwnerBootReports />} />
          <Route path="/owner/boot-reports" element={<Navigate to="/owner/dev-utility" replace />} />
          <Route path="/owner/profile" element={<OwnerProfile />} />
        </Route>
      </Route>
      <Route path="/owner" element={<Navigate to="/owner/dashboard" replace />} />

      {/* Default */}
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <LearnerAuthProvider>
          <OwnerAuthProvider>
            <AppRoutes />
            <VersionUpdateBanner />
            <Toaster
              theme="dark"
              position="top-center"
              toastOptions={{
                style: {
                  background: "#11141A",
                  border: "1px solid rgba(255,255,255,0.1)",
                  color: "#fff",
                  fontFamily: "'Jost', sans-serif",
                },
              }}
            />
          </OwnerAuthProvider>
        </LearnerAuthProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
